import React, { useState } from "react";
import type { IProfile } from "App/Modules/Models";
import { useTranslation } from "react-i18next";
import { Button } from "@material-ui/core";
import { UserProfileSecurityDialogTabs } from "./UserProfileSecurityTabs";
import type { AvailableSecurityTabs } from "./UserProfileSecurityTabs";
import { UserProfileSecurityDialogPassword } from "./UserProfileSecurityDialogPassword";

import "../_user-profile-dialog.scss";
import { UserProfileSecurityDialogAccessCode } from "./UserProfileSecurityDialogAccessCode";
import { UserProfileSecurityDialogTwoFactor } from "./UserProfileSecurityDialogTwoFactor";
import type { SaveDataTransferStatus } from "../../Types";

type UserProfileSecurityDialogProps = {
    profile?: IProfile;
    updateUser: (profile: IProfile) => void;
    onFormReset: () => void;
    saveProfileStatus: SaveDataTransferStatus;
    initialTab: AvailableSecurityTabs;
    onChange: (isDirty: boolean) => void;
    onClose: (event?: any, reason?: "backdropClick" | "escapeKeyDown" | "userCancelled") => void;
};

export const UserProfileSecurityDialog = (props: UserProfileSecurityDialogProps) => {
    const { initialTab, saveProfileStatus } = props;
    const [activeTab, setActiveTab] = useState<AvailableSecurityTabs>(initialTab || "ChangePassword");
    const [isDirty, setIsDirty] = useState(false);
    const [showRevertInformation, setShowRevertInformation] = useState(false);

    const { t } = useTranslation();

    const handleChangeTab = (tabName: AvailableSecurityTabs) => {
        if (isDirty) {
            setShowRevertInformation(true);
        } else {
            setActiveTab(tabName);
            props.onFormReset();
        }
    };

    const handleOnChange = (isFormDirty: boolean) => {
        if (showRevertInformation) {
            setShowRevertInformation(isFormDirty);
        }

        setIsDirty(isFormDirty);
        props.onChange(isFormDirty);
    };

    return (
        <>
            <div className="card dialog user-profile-dialog">
                <div className="dialog__content">
                    <div className="dialog__section dialog__section--full">
                        <h1 className="title title-h1">{t("Gui.Common.UserProfileSecurityDialog.MainHeader")}</h1>
                    </div>
                    <div className="dialog__section dialog__section--small">
                        <UserProfileSecurityDialogTabs activeTab={activeTab} handleChangeTab={handleChangeTab} />
                    </div>
                    <div className="dialog__section dialog__section--large dialog__section--flipped-spacing">
                        {activeTab === "ChangePassword" ? (
                            <UserProfileSecurityDialogPassword
                                profile={props.profile}
                                updateUser={props.updateUser}
                                onFormReset={props.onFormReset}
                                saveProfileStatus={saveProfileStatus}
                                onChange={handleOnChange}
                                showRevertInformation={showRevertInformation}
                            />
                        ) : null}
                        {activeTab === "ChangeAccessCode" ? (
                            <UserProfileSecurityDialogAccessCode
                                profile={props.profile}
                                updateUser={props.updateUser}
                                onFormReset={props.onFormReset}
                                saveProfileStatus={saveProfileStatus}
                                onChange={handleOnChange}
                                showRevertInformation={showRevertInformation}
                            />
                        ) : null}
                        {activeTab === "ChangeTwoFactor" ? (
                            <UserProfileSecurityDialogTwoFactor
                                profile={props.profile}
                                updateUser={props.updateUser}
                                onFormReset={props.onFormReset}
                                saveProfileStatus={saveProfileStatus}
                                onChange={handleOnChange}
                                showRevertInformation={showRevertInformation}
                            />
                        ) : null}
                    </div>
                </div>
                <div className="dialog__footer">
                    <Button
                        data-testid="close-dialog-button"
                        disabled={saveProfileStatus.isLoading}
                        type="button"
                        variant="text"
                        color="default"
                        size="medium"
                        onClick={(e) => props.onClose(e, "userCancelled")}
                    >
                        {t("Gui.Buttons.Close")}
                    </Button>
                </div>
            </div>
        </>
    );
};
